import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userService, CreateUserData } from '../../services/userService';
import { permissionsToArray, TEAM_PERMISSION_OPTIONS } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Security,
  People,
  History,
  Settings,
  Add,
  Edit,
  Delete,
  Visibility,
  Block,
  CheckCircle,
  Warning,
  // Aliased: an unaliased `Error` import shadows the global constructor for the
  // whole module, so every `new Error(...)` below throws "is not a constructor".
  Error as ErrorIcon,
  Info,
  Lock,
  Key,
  Shield,
  VerifiedUser,
  AdminPanelSettings,
  PersonAdd,
  SecurityUpdate,
  VpnKey,
  TwoWheeler,
} from '@mui/icons-material';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`security-tabpanel-${index}`}
      aria-labelledby={`security-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'lawyer' | 'paralegal' | 'assistant';
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: string;
  twoFactorEnabled: boolean;
  permissions: string[];
}

interface AuditLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  resource: string;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'failed' | 'warning';
}

interface SecuritySettings {
  twoFactorRequired: boolean;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
  };
  sessionTimeout: number;
  maxLoginAttempts: number;
  lockoutDuration?: number;
  ipWhitelist: string[];
  auditLogRetention: number;
  /** Wave P.5 — block staff downloads until attorney_reviewed / sent */
  requireAttorneyReviewBeforeOutbound?: boolean;
  nativeESignEnabled?: boolean;
  clientPaymentsEnabled?: boolean;
  portalCallsEnabled?: boolean;
  callRecordingEnabled?: boolean;
}

// User Dialog Component - Moved outside to prevent recreation
const UserDialog = React.memo(({ 
  open, 
  onClose, 
  userFormData, 
  setUserFormData, 
  selectedUser, 
  onSave 
}: {
  open: boolean;
  onClose: () => void;
  userFormData: {
    firstName: string;
    lastName: string;
    email: string;
    role: User['role'];
    permissions: string[];
    phone?: string;
  };
  setUserFormData: (data: any) => void;
  selectedUser: User | null;
  onSave: () => void;
}) => (
  <Dialog 
    open={open} 
    onClose={onClose} 
    maxWidth="sm" 
    fullWidth
    disableEnforceFocus
    disableAutoFocus
    keepMounted={false}
  >
    <DialogTitle>
      {selectedUser ? 'Edit User' : 'Add New User'}
    </DialogTitle>
          <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={userFormData.firstName}
                onChange={(e) => setUserFormData((prev: any) => ({ ...prev, firstName: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={userFormData.lastName}
                onChange={(e) => setUserFormData((prev: any) => ({ ...prev, lastName: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <TextField
            label="Email"
            type="email"
            value={userFormData.email}
            onChange={(e) => setUserFormData((prev: any) => ({ ...prev, email: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Phone (Optional)"
            value={userFormData.phone || ''}
            onChange={(e) => setUserFormData((prev: any) => ({ ...prev, phone: e.target.value }))}
            fullWidth
          />
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            value={userFormData.role}
            label="Role"
            onChange={(e) => setUserFormData((prev: any) => ({ ...prev, role: e.target.value as User['role'] }))}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="lawyer">Attorney</MenuItem>
            <MenuItem value="paralegal">Paralegal</MenuItem>
            <MenuItem value="assistant">Assistant</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Permissions</InputLabel>
          <Select
            multiple
            value={userFormData.permissions}
            label="Permissions"
            onChange={(e) => setUserFormData((prev: any) => ({ ...prev, permissions: e.target.value as string[] }))}
            renderValue={(selected) => (selected as string[]).join(', ')}
          >
            {TEAM_PERMISSION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onSave} variant="contained">
        {selectedUser ? 'Update' : 'Create'}
      </Button>
    </DialogActions>
  </Dialog>
));

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState({
    twoFactorRequired: true,
    passwordPolicy: {
      minLength: 12,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
    },
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8'],
    auditLogRetention: 90,
    requireAttorneyReviewBeforeOutbound: false,
    nativeESignEnabled: false,
    clientPaymentsEnabled: false,
    portalCallsEnabled: false,
    callRecordingEnabled: false,
  });
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'assistant' as User['role'],
    permissions: [] as string[],
    phone: '',
  });
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();

  // React Query hooks — tenantId in key prevents cross-tenant cache reuse
  const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['users', authUser?.tenantId],
    queryFn: () => userService.getUsers(),
    enabled: Boolean(authUser?.tenantId),
  });

  const { data: auditLogsData, isLoading: auditLogsLoading, error: auditLogsError } = useQuery({
    queryKey: ['auditLogs', authUser?.tenantId],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch('/api/audit/logs', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      return response.json();
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: CreateUserData) => userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: Partial<CreateUserData> }) =>
      userService.updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const suspendUserMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) => 
      userService.suspendUser(userId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => userService.resetUserPassword(userId),
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddUser = () => {
    setUserFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'assistant',
      permissions: [],
      phone: '',
    });
    setSelectedUser(null);
    setIsUserDialogOpen(true);
  };

  const handleEditUser = async (user: any) => {
    setUserFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
      role: user.role,
      permissions: permissionsToArray(user.permissions),
      phone: user.phone || '',
    });
    setSelectedUser(user);
    setIsUserDialogOpen(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const json = await response.json();
        const full = json.data || json;
        setSelectedUser(full);
        setUserFormData({
          firstName: full.firstName || '',
          lastName: full.lastName || '',
          email: full.email || '',
          role: full.role || user.role,
          permissions: permissionsToArray(full.permissions),
          phone: full.phone || '',
        });
      }
    } catch {
      /* list prefill is enough */
    }
  };

  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
    const status = user.isActive ? 'Active' : 'Inactive';
    const loginStatus = user.isFirstLogin ? 'First Login Required' : 'Normal';
    alert(`User Details:\nName: ${name}\nEmail: ${user.email}\nRole: ${user.role}\nStatus: ${status}\nLogin Status: ${loginStatus}`);
  };

  const handleSaveUser = async () => {
    try {
      if (selectedUser) {
        // Update existing user
        await updateUserMutation.mutateAsync({
          userId: selectedUser.id,
          userData: userFormData,
        });
        alert('User updated successfully!');
      } else {
        // Create new user
        const result = await createUserMutation.mutateAsync(userFormData);
        const created = result?.data || result;
        if (created?.temporaryPassword) {
          alert(
            `User created successfully!\n\nDemo temporary password (copy now):\n${created.temporaryPassword}\n\nEmail: ${created.email}\n\nSign in with this password, then set a permanent password on first login.`
          );
        } else {
          alert('User created successfully! A temporary password has been sent to their email.');
        }
      }
      
      setIsUserDialogOpen(false);
      setUserFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'assistant',
        permissions: [],
        phone: '',
      });
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user. Please try again.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUserMutation.mutateAsync(userId);
        alert('User deleted successfully!');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user. Please try again.');
      }
    }
  };

  const handleSuspendUser = async (userId: string, isActive: boolean) => {
    const action = isActive ? 'activate' : 'suspend';
    if (window.confirm(`Are you sure you want to ${action} this user?`)) {
      try {
        await suspendUserMutation.mutateAsync({ userId, isActive });
        alert(`User ${action}ed successfully!`);
      } catch (error) {
        console.error(`Error ${action}ing user:`, error);
        alert(`Error ${action}ing user. Please try again.`);
      }
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (window.confirm('Are you sure you want to reset this user\'s password?')) {
      try {
        const result = await resetPasswordMutation.mutateAsync(userId);
        const data = result?.data || result;
        if (data?.temporaryPassword) {
          alert(
            `Password reset successfully!\n\nDemo temporary password (copy now):\n${data.temporaryPassword}\n\nEmail: ${data.email}`
          );
        } else {
          alert('Password reset successfully! A new temporary password has been sent to the user\'s email.');
        }
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Error resetting password. Please try again.');
      }
    }
  };

  const handleEditSecuritySettings = () => {
    setSettingsFormData({
      twoFactorRequired: sampleSettings.twoFactorRequired,
      passwordPolicy: { ...sampleSettings.passwordPolicy },
      sessionTimeout: sampleSettings.sessionTimeout,
      maxLoginAttempts: sampleSettings.maxLoginAttempts,
      lockoutDuration: sampleSettings.lockoutDuration ?? 15,
      ipWhitelist: [...sampleSettings.ipWhitelist],
      auditLogRetention: sampleSettings.auditLogRetention,
      requireAttorneyReviewBeforeOutbound:
        Boolean(sampleSettings.requireAttorneyReviewBeforeOutbound),
      nativeESignEnabled: Boolean(sampleSettings.nativeESignEnabled),
      clientPaymentsEnabled: Boolean(sampleSettings.clientPaymentsEnabled),
      portalCallsEnabled: Boolean(sampleSettings.portalCallsEnabled),
      callRecordingEnabled: Boolean(sampleSettings.callRecordingEnabled),
    });
    setIsSettingsDialogOpen(true);
  };

  const handleSaveSecuritySettings = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        alert('No authentication token');
        return;
      }

      const response = await fetch('/api/security/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settingsFormData),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update security settings.'));
      }

      const result = await response.json();
      console.log('Security settings updated successfully:', result);
      
      // Refresh the settings data
      await refetchSettings();
      
      alert('Security settings updated successfully!');
      setIsSettingsDialogOpen(false);
    } catch (error) {
      console.error('Failed to update security settings:', error);
      alert(toUserErrorMessage(error, 'Failed to update security settings. Please try again.'));
    }
  };

  const handlePasswordPolicyChange = (field: string, value: any) => {
    setSettingsFormData(prev => ({
      ...prev,
      passwordPolicy: {
        ...prev.passwordPolicy,
        [field]: value
      }
    }));
  };

  const handleIpWhitelistChange = (index: number, value: string) => {
    const newWhitelist = [...settingsFormData.ipWhitelist];
    newWhitelist[index] = value;
    setSettingsFormData(prev => ({
      ...prev,
      ipWhitelist: newWhitelist
    }));
  };

  const addIpWhitelistEntry = () => {
    setSettingsFormData(prev => ({
      ...prev,
      ipWhitelist: [...prev.ipWhitelist, '']
    }));
  };

  const removeIpWhitelistEntry = (index: number) => {
    const newWhitelist = settingsFormData.ipWhitelist.filter((_, i) => i !== index);
    setSettingsFormData(prev => ({
      ...prev,
      ipWhitelist: newWhitelist
    }));
  };

  // Get users from API or use sample data as fallback
  const users = usersData?.data?.users || [];
  const isLoading = usersLoading;
  const error = usersError;

  // Get audit logs from API or use empty array as fallback
  const auditLogs = auditLogsData?.data?.auditLogs || [];

  // Fetch security settings from API
  const { data: securitySettings, isLoading: settingsLoading, refetch: refetchSettings } = useQuery({
    queryKey: ['securitySettings', authUser?.tenantId],
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch('/api/security/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch security settings');
      }

      const data = await response.json();
      return data.data;
    },
    onSuccess: (data) => {
      setSettingsFormData(data);
    },
  });

  const sampleSettings: SecuritySettings = securitySettings || {
    twoFactorRequired: false,
    passwordPolicy: {
      minLength: 12,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
    },
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    ipWhitelist: [],
    auditLogRetention: 90,
    requireAttorneyReviewBeforeOutbound: false,
    nativeESignEnabled: false,
    clientPaymentsEnabled: false,
    portalCallsEnabled: false,
    callRecordingEnabled: false,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'attorney': return 'primary';
      case 'paralegal': return 'secondary';
      case 'assistant': return 'info';
      default: return 'default';
    }
  };

  const getAuditStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'warning': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  const renderUserManagement = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" component="span">User Management</Typography>
              <Typography variant="body2" color="text.secondary" display="block">
                Prefer Team Management for full profiles — permissions here use the same role keys.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate('/users')}>
                Open Team Management
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddUser}
              >
                Add User
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>2FA</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>Loading users...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="error">Error loading users</Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user: any) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar>{user.firstName?.charAt(0) || user.email.charAt(0)}</Avatar>
                          <Box>
                            <Typography variant="subtitle2" component="span">
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={getRoleColor(user.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'active' : 'inactive'}
                          color={getStatusColor(user.isActive ? 'active' : 'inactive') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.isFirstLogin ? (
                          <Chip icon={<Warning />} label="First Login" color="warning" size="small" />
                        ) : (
                          <Chip icon={<CheckCircle />} label="Normal" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="span">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewUser(user)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.isActive ? "Suspend User" : "Activate User"}>
                            <IconButton 
                              size="small" 
                              color={user.isActive ? "warning" : "success"} 
                              onClick={() => handleSuspendUser(user.id, !user.isActive)}
                            >
                              {user.isActive ? <Block /> : <CheckCircle />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton size="small" color="warning" onClick={() => handleResetPassword(user.id)}>
                              <VpnKey />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const renderAuditLogs = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom component="span">
            Audit Logs
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>Loading audit logs...</Typography>
                    </TableCell>
                  </TableRow>
                ) : auditLogsError ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="error">Error loading audit logs</Typography>
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No audit logs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="subtitle2" component="span">
                          {log.user ? log.user.name : 'Unknown User'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="span">{log.action}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="span">{log.resource}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getAuditStatusIcon(log.status)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }} component="span">
                            {log.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="span">{log.ipAddress || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="span">
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const renderSecuritySettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom component="span">
            Security Settings
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Two-Factor Authentication
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={sampleSettings.twoFactorRequired}
                    onChange={() => {}}
                  />
                }
                label="Require 2FA for all users"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span">
                Enforce two-factor authentication for all user accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Document outbound policy
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(sampleSettings.requireAttorneyReviewBeforeOutbound)}
                    onChange={() => {}}
                  />
                }
                label="Require attorney review before download"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span">
                {sampleSettings.requireAttorneyReviewBeforeOutbound
                  ? 'Enabled — staff downloads blocked until attorney-reviewed'
                  : 'Disabled — merge-field guard only (portal still requires review)'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Native e-signature
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(sampleSettings.nativeESignEnabled)}
                    onChange={() => {}}
                  />
                }
                label="Enable in-portal e-sign (no DocuSign)"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span">
                {sampleSettings.nativeESignEnabled
                  ? 'Enabled — Case Folder Request signature is available for eligible PDFs'
                  : 'Disabled — turn on in Edit Policy. Not for wills, notary, or settlement releases.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Client invoice payments
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(sampleSettings.clientPaymentsEnabled)}
                    onChange={() => {}}
                  />
                }
                label="Enable portal Pay on invoices"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span">
                {sampleSettings.clientPaymentsEnabled
                  ? 'Enabled — clients can pay sent invoices by card (ACH when Stripe allows). Operating AR only, not trust.'
                  : 'Disabled — turn on in Edit Policy (uses Tenant feature paymentGateway).'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Portal calls
              </Typography>
              <FormControlLabel
                control={<Switch checked={Boolean(sampleSettings.portalCallsEnabled)} onChange={() => {}} />}
                label="Voice / video calls with portal clients"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(sampleSettings.callRecordingEnabled)} onChange={() => {}} />}
                label="Call recording (with client consent)"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span">
                {sampleSettings.portalCallsEnabled
                  ? 'Enabled — staff can call signed-in clients from a matter or client file. Calls are encrypted end to end between browsers.'
                  : 'Disabled — turn on in Edit Policy.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Password Policy
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Minimum length: {sampleSettings.passwordPolicy.minLength} characters
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Require uppercase: {sampleSettings.passwordPolicy.requireUppercase ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Require numbers: {sampleSettings.passwordPolicy.requireNumbers ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Require special characters: {sampleSettings.passwordPolicy.requireSpecialChars ? 'Yes' : 'No'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditSecuritySettings}
                sx={{ mt: 1 }}
              >
                Edit Policy
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Session Management
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Session timeout: {sampleSettings.sessionTimeout} hours
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Max login attempts: {sampleSettings.maxLoginAttempts}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditSecuritySettings}
                sx={{ mt: 1 }}
              >
                Edit Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="span">
                Access Control
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                IP Whitelist: {sampleSettings.ipWhitelist.length} entries
              </Typography>
              <Typography variant="body2" gutterBottom component="span">
                Audit log retention: {sampleSettings.auditLogRetention} days
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditSecuritySettings}
                sx={{ mt: 1 }}
              >
                Edit Access Control
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };



  // Security Settings Dialog Component
  const SecuritySettingsDialog = React.memo(() => (
    <Dialog open={isSettingsDialogOpen} onClose={() => setIsSettingsDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Security Settings Configuration
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Two-Factor Authentication */}
          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Two-Factor Authentication
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settingsFormData.twoFactorRequired}
                  onChange={(e) => setSettingsFormData({ ...settingsFormData, twoFactorRequired: e.target.checked })}
                />
              }
              label="Require 2FA for all users"
            />
          </Box>

          {/* Wave P.5 — attorney review before outbound */}
          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Document outbound policy
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settingsFormData.requireAttorneyReviewBeforeOutbound)}
                  onChange={(e) =>
                    setSettingsFormData({
                      ...settingsFormData,
                      requireAttorneyReviewBeforeOutbound: e.target.checked,
                    })
                  }
                />
              }
              label="Require attorney review before staff download / send"
            />
            <Typography variant="body2" color="text.secondary" component="span">
              When enabled, documents must be marked attorney-reviewed (or sent) before download.
              Client portal already requires review regardless of this setting.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Native e-signature
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settingsFormData.nativeESignEnabled)}
                  onChange={(e) =>
                    setSettingsFormData({
                      ...settingsFormData,
                      nativeESignEnabled: e.target.checked,
                    })
                  }
                />
              }
              label="Enable native e-sign for portal clients"
            />
            <Typography variant="body2" color="text.secondary" component="span">
              Send-for-sign is always attorney-reviewed. Signature packages are retained with the
              matter file, not the 90-day audit log.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Client invoice payments
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settingsFormData.clientPaymentsEnabled)}
                  onChange={(e) =>
                    setSettingsFormData({
                      ...settingsFormData,
                      clientPaymentsEnabled: e.target.checked,
                    })
                  }
                />
              }
              label="Enable portal Pay on invoices (Stripe Checkout)"
            />
            <Typography variant="body2" color="text.secondary" component="span">
              Payments apply to the invoice balance. Not a trust/IOLTA deposit. Same Stripe keys as
              SaaS billing; checkout metadata purpose=client_invoice.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Portal calls
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settingsFormData.portalCallsEnabled)}
                  onChange={(e) =>
                    setSettingsFormData({
                      ...settingsFormData,
                      portalCallsEnabled: e.target.checked,
                      callRecordingEnabled: e.target.checked ? settingsFormData.callRecordingEnabled : false,
                    })
                  }
                />
              }
              label="Enable voice / video calls with portal clients"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(settingsFormData.callRecordingEnabled)}
                  disabled={!settingsFormData.portalCallsEnabled}
                  onChange={(e) =>
                    setSettingsFormData({
                      ...settingsFormData,
                      callRecordingEnabled: e.target.checked,
                    })
                  }
                />
              }
              label="Allow call recording (client must agree on every call)"
            />
            <Typography variant="body2" color="text.secondary" component="span">
              Staff ring a client who is signed in to the portal. Audio and video go browser to browser,
              encrypted. A recording starts only after the client agrees; it is saved to the matter as a
              confidential, privileged document and is never shown in the portal. Some states require every
              party&apos;s consent to record — the consent prompt covers that, but check your jurisdiction.
            </Typography>
          </Box>

          {/* Password Policy */}
          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Password Policy
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Length"
                  type="number"
                  value={settingsFormData.passwordPolicy.minLength}
                  onChange={(e) => handlePasswordPolicyChange('minLength', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settingsFormData.passwordPolicy.requireUppercase}
                      onChange={(e) => handlePasswordPolicyChange('requireUppercase', e.target.checked)}
                    />
                  }
                  label="Require Uppercase"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settingsFormData.passwordPolicy.requireLowercase}
                      onChange={(e) => handlePasswordPolicyChange('requireLowercase', e.target.checked)}
                    />
                  }
                  label="Require Lowercase"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settingsFormData.passwordPolicy.requireNumbers}
                      onChange={(e) => handlePasswordPolicyChange('requireNumbers', e.target.checked)}
                    />
                  }
                  label="Require Numbers"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settingsFormData.passwordPolicy.requireSpecialChars}
                      onChange={(e) => handlePasswordPolicyChange('requireSpecialChars', e.target.checked)}
                    />
                  }
                  label="Require Special Characters"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Session Management */}
          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Session Management
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Session Timeout (hours)"
                  type="number"
                  value={settingsFormData.sessionTimeout}
                  onChange={(e) => setSettingsFormData({ ...settingsFormData, sessionTimeout: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Login Attempts"
                  type="number"
                  value={settingsFormData.maxLoginAttempts}
                  onChange={(e) => setSettingsFormData({ ...settingsFormData, maxLoginAttempts: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Lockout Duration (minutes)"
                  type="number"
                  value={settingsFormData.lockoutDuration}
                  onChange={(e) => setSettingsFormData({ ...settingsFormData, lockoutDuration: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Access Control */}
          <Box>
            <Typography variant="h6" gutterBottom component="span">
              Access Control
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom component="span">
                  IP Whitelist
                </Typography>
                {settingsFormData.ipWhitelist.map((ip, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      label={`IP Address ${index + 1}`}
                      value={ip}
                      onChange={(e) => handleIpWhitelistChange(index, e.target.value)}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => removeIpWhitelistEntry(index)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addIpWhitelistEntry}
                  sx={{ mt: 1 }}
                >
                  Add IP Address
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Audit Log Retention (days)"
                  type="number"
                  value={settingsFormData.auditLogRetention}
                  onChange={(e) => setSettingsFormData({ ...settingsFormData, auditLogRetention: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsSettingsDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveSecuritySettings} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  ));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Security sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Security & Access Control
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="security tabs">
          <Tab label="User Management" icon={<People />} iconPosition="start" />
          <Tab label="Audit Logs" icon={<History />} iconPosition="start" />
          <Tab label="Security Settings" icon={<Settings />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderUserManagement()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderAuditLogs()}
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        {renderSecuritySettings()}
      </TabPanel>

      {/* Dialogs */}
      <UserDialog 
        open={isUserDialogOpen}
        onClose={() => setIsUserDialogOpen(false)}
        userFormData={userFormData}
        setUserFormData={setUserFormData}
        selectedUser={selectedUser}
        onSave={handleSaveUser}
      />
      <SecuritySettingsDialog />
    </Box>
  );
};

export default SecurityPage; 